import React, { FC, ReactNode, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTrialStatus } from '../hooks/useTrialStatus';
import { ROUTE_SOURCES } from '../constants/routeSources';
import { Box, Button, Typography, Alert } from '@mui/material';
import { usePersistJwtFromQuery } from 'src/hooks/usePersistJwtFromQuery';

interface TrialGuardProps {
  children: ReactNode;
}

export const TrialGuard: FC<TrialGuardProps> = ({ children }) => {
  // Persistenza JWT (da callback esterne)
  usePersistJwtFromQuery();
  const { shouldFreeze, isTrialExpired, loading } = useTrialStatus();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If trial is expired and user should be frozen, redirect to settings/account page
    if (!loading && (shouldFreeze || isTrialExpired)) {
      const isSettingsPage = location.pathname.includes('/settings');
      if (!isSettingsPage) {
        navigate(`/${ROUTE_SOURCES.SETTINGS}?tab=account&trial=expired`);
      }
    }
  }, [shouldFreeze, isTrialExpired, loading, navigate, location]);

  // Se sta caricando, mostra i children (evita flash di redirect)
  if (loading) {
    return <>{children}</>;
  }

  // If trial is expired and we're NOT on settings page, show non-interactive overlay
  if (
    (shouldFreeze || isTrialExpired) &&
    !location.pathname.includes('/settings')
  ) {
    return (
      <Box sx={{ position: 'relative' }}>
        <Box sx={{ filter: 'grayscale(1)', pointerEvents: 'none' }}>
          {children}
        </Box>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255,255,255,0.8)',
            p: 3,
          }}
        >
          <Box sx={{ maxWidth: 520 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Your trial period has ended. Some features are blocked.
            </Alert>
            <Typography variant="h5" sx={{ mb: 1 }}>
              Upgrade plan to continue
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              To regain full access, switch to a paid plan.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() =>
                navigate(`/${ROUTE_SOURCES.SETTINGS}?tab=account&trial=expired`)
              }
            >
              Vai alle Impostazioni
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  // Altrimenti mostra i children normalmente
  return <>{children}</>;
};
